import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '@/lib/auth';
import { Logo } from '@/components/ui/Logo';
import { Award, Menu, X, SignOut } from '@/components/ui/Icons';
import { cn } from '@/lib/cn';

const NAV = [
  { to: '/admin', label: 'Dashboard', end: true },
  { to: '/admin/quotes', label: 'Quotes' },
  { to: '/admin/email-templates', label: 'Emails' },
];
const SUPER_NAV = [
  { to: '/admin/super/events', label: 'Activity' },
  { to: '/admin/super/login-attempts', label: 'Logins' },
  { to: '/admin/super/audit', label: 'Audit' },
];

export function AdminLayout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-paper-50 text-ink-900">
      <Head>
        <meta name="robots" content="noindex,nofollow" />
      </Head>

      <header className="sticky top-0 z-30 border-b border-navy-700 bg-navy-900 text-paper-0 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.6)]">
        <div className="container-page flex h-16 items-center gap-4 lg:h-[68px]">
          <Link
            to="/admin"
            className="flex shrink-0 items-center gap-3 transition hover:opacity-90"
            aria-label="Bliss admin home"
          >
            <Logo decorative interactive className="h-10 lg:h-11" />
            <span className="hidden font-display text-base font-semibold tracking-wide text-gold-300 md:inline">
              Bliss Admin
            </span>
          </Link>

          <nav className="ml-2 hidden items-center gap-1 text-sm md:flex" aria-label="Admin">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-3 py-2 transition',
                    isActive
                      ? 'bg-navy-800 text-gold-300 font-semibold'
                      : 'text-paper-0/85 hover:bg-navy-800 hover:text-gold-300',
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
            {user?.is_super && (
              <>
                <span className="mx-1 h-5 w-px bg-navy-700" aria-hidden="true" />
                {SUPER_NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    className={({ isActive }) =>
                      cn(
                        'rounded-md px-3 py-2 transition',
                        isActive
                          ? 'bg-gold-400/15 text-gold-300 font-semibold'
                          : 'text-paper-0/70 hover:bg-navy-800 hover:text-gold-300',
                      )
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
              </>
            )}
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-3">
            {user && (
              <div className="hidden items-center gap-2 sm:flex">
                <Avatar user={user} />
                <div className="hidden text-right md:block">
                  <p className="text-xs leading-tight text-paper-0/60">Signed in as</p>
                  <p className="text-sm font-semibold leading-tight text-paper-0">
                    {user.name || user.email}
                  </p>
                </div>
                {user.is_super && (
                  <span className="hidden items-center gap-1 rounded-full border border-gold-400/60 bg-gold-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gold-300 md:inline-flex">
                    <Award className="h-3 w-3" /> Super
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={async () => { await signOut(); navigate('/admin/login'); }}
              className="hidden items-center gap-2 rounded-md border border-gold-400/60 px-3 py-1.5 text-sm font-semibold text-gold-300 transition hover:bg-gold-400 hover:text-navy-900 md:inline-flex"
            >
              <SignOut className="h-3.5 w-3.5" />
              Sign out
            </button>
            <button
              type="button"
              className="grid h-10 w-10 place-items-center rounded-md text-paper-0 transition hover:bg-navy-800 md:hidden"
              onClick={() => setDrawerOpen((v) => !v)}
              aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={drawerOpen}
            >
              {drawerOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {drawerOpen && (
            <motion.div
              key="admin-drawer"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="border-t border-navy-700 bg-navy-900 md:hidden"
            >
              <div className="container-page space-y-1 py-4">
                {user && (
                  <div className="mb-3 flex items-center gap-3 rounded-md bg-navy-800 p-3">
                    <Avatar user={user} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-paper-0">{user.name || user.email}</p>
                      <p className="truncate text-xs text-paper-0/70">{user.email}</p>
                    </div>
                    {user.is_super && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-gold-400/60 bg-gold-400/10 px-2 py-0.5 text-[10px] font-semibold text-gold-300">
                        <Award className="h-3 w-3" /> Super
                      </span>
                    )}
                  </div>
                )}
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={() => setDrawerOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'block rounded-md px-3 py-2 text-sm transition',
                        isActive ? 'bg-navy-800 text-gold-300 font-semibold' : 'text-paper-0/85 hover:bg-navy-800',
                      )
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
                {user?.is_super && (
                  <>
                    <p className="mt-3 px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold-300/80">
                      Super-admin
                    </p>
                    {SUPER_NAV.map((n) => (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        onClick={() => setDrawerOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            'block rounded-md px-3 py-2 text-sm transition',
                            isActive ? 'bg-gold-400/15 text-gold-300 font-semibold' : 'text-paper-0/70 hover:bg-navy-800',
                          )
                        }
                      >
                        {n.label}
                      </NavLink>
                    ))}
                  </>
                )}
                <button
                  type="button"
                  onClick={async () => { setDrawerOpen(false); await signOut(); navigate('/admin/login'); }}
                  className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md border border-gold-400/60 px-3 py-2 text-sm font-semibold text-gold-300"
                >
                  <SignOut className="h-3.5 w-3.5" />
                  Sign out
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="flex-1">
        <div className="container-page py-8 lg:py-10">
          <Outlet />
        </div>
      </main>

      <footer className="border-t border-paper-100 bg-paper-0">
        <div className="container-page flex flex-col items-start justify-between gap-1 py-4 text-xs text-ink-700 sm:flex-row sm:items-center">
          <p>Bliss Building Contractors · Admin</p>
          <p>Signed in as {user?.email ?? '-'}</p>
        </div>
      </footer>
    </div>
  );
}

function Avatar({ user }: { user: { email: string; name: string; picture?: string | null } }) {
  if (user.picture) {
    return (
      <img
        src={user.picture}
        alt=""
        className="h-9 w-9 shrink-0 rounded-full ring-2 ring-gold-400/40"
        referrerPolicy="no-referrer"
      />
    );
  }
  const initial = (user.name || user.email).charAt(0).toUpperCase();
  return (
    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gold-400 font-semibold text-navy-900">
      {initial}
    </div>
  );
}
